import React, { useEffect } from "react";
import { cn } from "./index";

/**
 * Filtros avançados unificados (Phase 8 — Safe Zap v2.0).
 * - FilterDrawer: painel lateral com seções de filtro combináveis.
 * - FilterChips: barra horizontal de chips removíveis representando filtros ativos.
 * - FilterSection: agrupador com título + conteúdo customizável.
 * - useEscClose: utilitário para fechar com Esc.
 */

export function useEscClose(open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);
}

export function FilterDrawer({
  open,
  onClose,
  title = "Filtros",
  onApply,
  onReset,
  applyLabel = "Aplicar filtros",
  resetLabel = "Limpar tudo",
  children,
  activeCount = 0,
}) {
  useEscClose(open, onClose);

  return (
    <>
      <div
        className={cn("uiFilterBackdrop", open && "uiFilterBackdrop-show")}
        onClick={onClose}
        aria-hidden={!open}
      />
      <aside
        className={cn("uiFilterDrawer", open && "uiFilterDrawer-show")}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="uiFilterDrawer-head">
          <div>
            <h3>{title}</h3>
            {activeCount > 0 && (
              <span className="uiFilterDrawer-badge">{activeCount} ativo{activeCount > 1 ? "s" : ""}</span>
            )}
          </div>
          <button type="button" className="uiFilterDrawer-close" onClick={onClose} aria-label="Fechar">×</button>
        </header>

        <div className="uiFilterDrawer-body">
          {children}
        </div>

        <footer className="uiFilterDrawer-foot">
          <button type="button" className="uiFilterDrawer-reset" onClick={onReset}>
            {resetLabel}
          </button>
          <button type="button" className="uiFilterDrawer-apply" onClick={onApply}>
            {applyLabel}
          </button>
        </footer>
      </aside>
    </>
  );
}

export function FilterSection({ title, hint, children, className = "" }) {
  return (
    <section className={cn("uiFilterSection", className)}>
      <header>
        <h4>{title}</h4>
        {hint && <small>{hint}</small>}
      </header>
      <div className="uiFilterSection-body">{children}</div>
    </section>
  );
}

/**
 * Multi-select pills (visual segmented control with multi).
 * options: [{ value, label, count? }]
 */
export function FilterChipGroup({ options = [], values = [], onChange, multi = true }) {
  function toggle(v) {
    if (!multi) {
      onChange?.(values[0] === v ? [] : [v]);
      return;
    }
    if (values.includes(v)) onChange?.(values.filter((x) => x !== v));
    else onChange?.([...values, v]);
  }
  return (
    <div className="uiFilterChipGroup" role="group">
      {options.map((opt) => {
        const active = values.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            className={cn("uiFilterChip", active && "uiFilterChip-active")}
            onClick={() => toggle(opt.value)}
            aria-pressed={active}
          >
            <span>{opt.label}</span>
            {opt.count != null && <em>{opt.count}</em>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Active filters bar — shows removable chips for currently applied filters.
 * filters: [{ key, label, onRemove }]
 */
export function ActiveFilters({ filters = [], onClear }) {
  if (!filters.length) return null;
  return (
    <div className="uiActiveFilters">
      {filters.map((f) => (
        <button
          key={f.key}
          type="button"
          className="uiActiveFilter"
          onClick={f.onRemove}
          title="Remover filtro"
        >
          <span>{f.label}</span>
          <em>×</em>
        </button>
      ))}
      {onClear && filters.length > 1 && (
        <button type="button" className="uiActiveFilters-clear" onClick={onClear}>
          Limpar todos
        </button>
      )}
    </div>
  );
}

/**
 * Date range input (simple two `<input type="date">`).
 */
export function FilterDateRange({ from, to, onChange }) {
  return (
    <div className="uiFilterDateRange">
      <label>
        <span>De</span>
        <input
          type="date"
          value={from || ""}
          onChange={(e) => onChange?.({ from: e.target.value || null, to })}
        />
      </label>
      <label>
        <span>Até</span>
        <input
          type="date"
          value={to || ""}
          onChange={(e) => onChange?.({ from, to: e.target.value || null })}
        />
      </label>
    </div>
  );
}
